/**
 * Affiliate Payouts Ledger — Phase-7 read-side.
 *
 * Renders the live earnings ledger for active affiliates, fed by the
 * /api/me/affiliate-payouts endpoint. The endpoint reads affiliate_payouts
 * rows that the invoice.payment_succeeded webhook writes at the
 * affiliate's currently-effective rate (25% base or 30% performance).
 *
 * Hides itself for non-affiliates (the AffiliateUpgrade card handles that
 * surface). Shows a "cash payouts coming soon" tag because Stripe Connect
 * wiring for affiliates is the next P1 — until then the rows accrue as
 * platform-held credit.
 */
import { useEffect, useState } from "react";
import { CurrencyDollar, ChartLineUp, Hourglass, CheckCircle, Crown, ArrowsClockwise, ArrowUUpLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmt$ = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
};

const StatTile = ({ icon, label, value, sub, tone = "neutral", testid }) => {
  const toneBg = {
    neutral: "bg-white",
    success: "bg-[#E8F0E9]",
    warning: "bg-[#F8F1E9]",
    accent: "bg-[#FBE9E0]",
  }[tone];
  return (
    <div className={`${toneBg} border hairline rounded-md p-3.5`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
        {icon}{label}
      </div>
      <div className="font-serif-display text-2xl text-[#1C1B1A] tabular-nums mt-1">{value}</div>
      {sub && <div className="text-[11px] text-[#6B6A68] mt-0.5">{sub}</div>}
    </div>
  );
};

export default function AffiliatePayoutsLedger() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await api.get("/me/affiliate-payouts");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load Partner earnings.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(true); }, []);

  if (loading) {
    return <div className="text-[#6B6A68] text-sm py-2" data-testid="affiliate-payouts-loading">Loading earnings…</div>;
  }
  // Non-affiliates see nothing here — AffiliateUpgrade handles that surface.
  if (!data || !data.is_affiliate) return null;

  const isPerformance = data.effective_rev_share_pct >= 30;

  return (
    <div className="bg-white border hairline rounded-xl p-5 sm:p-6" data-testid="affiliate-payouts-ledger">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#C16E5A] mb-1">
            <Crown size={12} weight="duotone" />
            Partner earnings · Live
          </div>
          <h3 className="font-serif-display text-2xl text-[#1C1B1A]">
            Your rev-share ledger
            <span className="ml-2 text-[11px] uppercase tracking-[0.16em] align-middle text-[#4A5D4E]">
              {data.effective_rev_share_pct}% rate
              {isPerformance && <span className="ml-1 text-[#C16E5A]">· performance</span>}
            </span>
          </h3>
          <p className="text-xs text-[#6B6A68] mt-1">
            Auto-recorded every time a creator you referred pays their monthly subscription.
            The rate above is what gets applied at payout time, not just on the join page.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(false)}
          disabled={refreshing}
          data-testid="affiliate-payouts-refresh"
          className="text-xs text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1.5 disabled:opacity-50"
        >
          <ArrowsClockwise size={12} weight="bold" className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4">
        <StatTile
          icon={<ChartLineUp size={11} weight="duotone" />} label="This month"
          value={fmt$(data.earned_this_month_cents)}
          tone="accent"
          testid="affiliate-payouts-month"
        />
        <StatTile
          icon={<CurrencyDollar size={11} weight="duotone" />} label="Lifetime earned"
          value={fmt$(data.total_earned_cents)}
          tone="success"
          testid="affiliate-payouts-lifetime"
        />
        <StatTile
          icon={<Hourglass size={11} weight="duotone" />} label="Pending payout"
          value={fmt$(data.pending_cents)}
          sub="Earned · awaiting Connect"
          tone="warning"
          testid="affiliate-payouts-pending"
        />
        <StatTile
          icon={<CheckCircle size={11} weight="duotone" />} label="Paid out"
          value={fmt$(data.paid_cents)}
          sub="Wired via Stripe"
          testid="affiliate-payouts-paid"
        />
      </div>

      {/* Cash-payouts notice retired Feb 20, 2026 — Stripe Connect cash
          wiring is live (hourly auto-sweep + manual "Run payouts now").
          Notice block deleted. */}

      {/* Jun 2026 — reversal exposure. Only rendered when there IS something
          reversed, so a clean ledger stays clean. */}
      {(data.reversed_cents > 0 || data.unrecovered_cents > 0) && (
        <div
          className="mb-4 rounded-md border border-[#E4C9BE] bg-[#FBF1ED] px-3.5 py-3"
          data-testid="affiliate-reversal-summary"
        >
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#A8662F]">
            <ArrowUUpLeft size={11} weight="duotone" />
            Reversed on refunded payments
          </div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-5 gap-y-1">
            <span className="font-serif-display text-xl tabular-nums text-[#1C1B1A]"
              data-testid="affiliate-reversed-total">
              {fmt$(data.reversed_cents)}
            </span>
            {data.unrecovered_cents > 0 && (
              <span className="text-[11px] text-[#6B6A68]" data-testid="affiliate-unrecovered-total">
                incl. <strong className="text-[#A8662F]">{fmt$(data.unrecovered_cents)}</strong> already
                paid to you and recorded as unrecovered
              </span>
            )}
          </div>
          <p className="text-[11px] text-[#6B6A68] mt-1.5 leading-relaxed">
            When a customer is refunded or wins a chargeback, the rev-share on that one sale is
            reversed in proportion to what they got back. We never debit your bank account and we
            never take it from your other balances or your future earnings.
          </p>
        </div>
      )}

      {data.rows.length === 0 ? (
        <div className="text-center py-8 text-sm text-[#6B6A68] bg-[#F0F1EE] rounded-md" data-testid="affiliate-payouts-empty">
          No earnings yet. Once one of your referrals pays their first invoice, the row lands here in real time.
        </div>
      ) : (
        <div className="border hairline rounded-md overflow-hidden" data-testid="affiliate-payouts-table">
          {/* Feb 21, 2026 — added WHO column, grid is 14 cols.
              Date 3 · Who 3 · Source 3 · Rate 1 · Earned 2 · Status 2 */}
          <div className="grid grid-cols-14 px-3 py-2 bg-[#F0F1EE] text-[10px] uppercase tracking-[0.15em] text-[#6B6A68]"
            style={{gridTemplateColumns: "repeat(14, minmax(0, 1fr))"}}>
            <div className="col-span-3">Date</div>
            <div className="col-span-3">Who</div>
            <div className="col-span-3">Source</div>
            <div className="col-span-1 text-right">Rate</div>
            <div className="col-span-2 text-right">Earned</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          {/* Caps the visible list at ~3 rows tall (≈40px each) then
              scrolls. Prevents the ledger from growing unbounded as the
              affiliate accumulates hundreds of monthly invoices. */}
          <div className="max-h-[140px] overflow-y-auto" data-testid="affiliate-payouts-scroll">
            {data.rows.map((r) => (
              <div
                key={r.id}
                className="grid grid-cols-14 px-3 py-2 text-[12px] border-t hairline tabular-nums hover:bg-[#FDFCF8]"
                style={{gridTemplateColumns: "repeat(14, minmax(0, 1fr))"}}
                data-testid={`affiliate-payout-row-${r.id}`}
              >
                <div className="col-span-3 text-[#1C1B1A]">{fmtDate(r.created_at)}</div>
                <div className="col-span-3 text-[#1C1B1A] truncate" title={r.invitee_email || ""}>
                  {r.invitee_name || (r.invitee_email ? r.invitee_email.split("@")[0] : "—")}
                </div>
                <div className="col-span-3 text-[#6B6A68] truncate">
                  {/* Feb 21, 2026 — surface source_kind so retro credits
                      are visually distinct from live recurring payouts. */}
                  {r.source_kind === "affiliate_gate" || r.source_kind === "affiliate_gate_retro" ? (
                    <span className="text-[#C16E5A] font-medium">
                      {r.source_kind === "affiliate_gate_retro" ? "Retro gate" : "Gate"} · {fmt$(r.source_amount_cents)}
                    </span>
                  ) : r.source_kind === "platform_subscription_retro" ? (
                    <span className="text-[#A8662F]">
                      Retro sub · {fmt$(r.source_amount_cents)}
                    </span>
                  ) : r.source_kind === "affiliate_gate_forfeit" ? (
                    <span className="text-[#6B6A68]">Forfeited · {fmt$(r.source_amount_cents)}</span>
                  ) : r.source_kind === "dfy_delivery" ? (
                    <span className="text-[#4A5D4E]">DFY delivery · {fmt$(r.source_amount_cents)}</span>
                  ) : r.is_reversal ? (
                    <span className="text-[#A03D22]">Reversal · refunded sale</span>
                  ) : (
                    <>Invoice · {fmt$(r.source_amount_cents)}</>
                  )}
                </div>
                <div className="col-span-1 text-right text-[#4A5D4E]">{r.rev_share_pct}%</div>
                <div className="col-span-2 text-right font-medium text-[#1C1B1A]">
                  {/* A reversal row is negative money; a partly-reversed row
                      shows what is still valid with the original struck out. */}
                  {r.status === "unrecovered" || (r.payout_cents || 0) < 0 ? (
                    <span className="text-[#A8662F]" data-testid={`affiliate-payout-amount-${r.id}`}>
                      −{fmt$(Math.abs(r.payout_cents || 0))}
                    </span>
                  ) : (r.reversed_cents || 0) > 0 ? (
                    <span data-testid={`affiliate-payout-amount-${r.id}`}>
                      <span className="text-[#9C9A94] line-through mr-1">
                        {fmt$(r.original_payout_cents)}
                      </span>
                      {fmt$(r.payout_cents)}
                    </span>
                  ) : (
                    <span data-testid={`affiliate-payout-amount-${r.id}`}>{fmt$(r.payout_cents)}</span>
                  )}
                </div>
                <div className="col-span-2 text-right">
                  {(() => {
                    const partial = (r.reversed_cents || 0) > 0 && (r.payout_cents || 0) > 0;
                    const label =
                      r.status === "unrecovered" ? "unrecovered"
                        : r.status === "reversed" ? "reversed"
                          : partial ? "part reversed"
                            : r.financial_review ? "under review"
                              : r.status === "held" ? "queued"
                                : (r.status || "earned");
                    const cls =
                      r.status === "reversed" || r.status === "unrecovered"
                        ? "bg-[#F6E2DA] text-[#A03D22]"
                        : partial || r.financial_review
                          ? "bg-[#FBF1ED] text-[#A8662F] border border-[#E4C9BE]"
                          : r.status === "paid" || r.status === "transferred"
                            ? "bg-[#E8F0E9] text-[#4A5D4E]"
                            : r.status === "earned"
                              ? "bg-[#F8F1E9] text-[#A8662F]"
                              : r.status === "held"
                                ? "bg-[#FDF1ED] text-[#A8662F]"
                                : "bg-[#F0F1EE] text-[#6B6A68]";
                    const why = r.reversal_reason
                      ? `${r.reversal_reason} · ${r.source_kind || "sale"}`
                      : (r.status_reason || "");
                    return (
                      <span
                        className={`text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded ${cls}`}
                        title={why}
                        data-testid={`affiliate-payout-status-${r.id}`}
                      >
                        {label}
                      </span>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
          {data.rows.length > 3 && (
            <div className="px-3 py-1.5 text-[10px] text-[#6B6A68] bg-[#FDFCF8] border-t hairline text-center">
              Showing {data.rows.length} entries — scroll for more
            </div>
          )}
        </div>
      )}
    </div>
  );
}
